import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, TrendingUp } from "lucide-react";

const EnergyContractsSection = () => {
  const contracts = [
    {
      title: "Dynamisch Stroom & Gas",
      subtitle: "Standaard dynamisch energiecontract",
      description: "Ideaal voor wie wil profiteren van de beursprijzen zonder extra's.",
      stroomprijs: {
        kale: "Actuele beursprijs (gem. € 0,07 per kWh)",
        inkoopvergoeding: "€ 0,035 per kWh",
        vastrecht: "€ 7 per maand"
      },
      gasprijs: {
        kale: "Actuele beursprijs (gem. € 0,34 per m³)",
        inkoopvergoeding: "€ 0,09 per m³",
        vastrecht: "€ 7 per maand"
      },
      handelen: false,
      icon: <Zap className="h-8 w-8 text-primary" />
    },
    {
      title: "SMART Dynamisch Stroom & Gas",
      subtitle: "Voor de slimme energiegebruiker",
      description: "Ontworpen voor wie actief wil handelen op de energiemarkt.",
      stroomprijs: {
        kale: "Actuele beursprijs (gem. € 0,07 per kWh)",
        inkoopvergoeding: "€ 0,04 per kWh",
        vastrecht: "€ 10 per maand"
      },
      gasprijs: {
        kale: "Actuele beursprijs (gem. € 0,34 per m³)",
        inkoopvergoeding: "€ 0,09 per m³",
        vastrecht: "€ 7 per maand"
      },
      handelen: true,
      handelsprijs: "€ 16,50 per maand extra",
      icon: <TrendingUp className="h-8 w-8 text-secondary" />
    }
  ];

  const benefits = [
    "Geen boetes voor zonnepanelen",
    "Flexibele, maandelijkse opzegtermijn", 
    "Slim energie in- en verkopen tegen dynamische tarieven"
  ];

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Onze Energieproducten: Altijd de Actuele Beursprijs
          </h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto mb-8">
            Bij EasyStroom bieden we flexibele, dynamische energiecontracten aan. Dit betekent dat u profiteert van de actuele beursprijzen die per uur verschillen, zowel voor stroom als voor gas.
          </p>
          
          {/* Shared benefits */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-6 max-w-3xl mx-auto mb-8">
            <h3 className="text-xl font-semibold mb-4">Voordelen van beide contracten:</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground">
            Alle genoemde bedragen zijn exclusief BTW
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {contracts.map((contract, index) => (
            <Card key={index} className="h-full hover:shadow-lg transition-shadow">
              <div className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  {contract.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2">{contract.title}</h3>
                <p className="text-primary font-semibold">{contract.subtitle}</p>
                <p className="text-muted-foreground">{contract.description}</p>
              </div>
              <div className="p-6 pt-0 space-y-6">
                {/* Stroomprijs */}
                <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-3">Stroomprijs</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Kale prijs:</span>
                      <span className="font-medium">{contract.stroomprijs.kale}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Inkoopvergoeding:</span>
                      <span className="font-medium">{contract.stroomprijs.inkoopvergoeding}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Vastrecht:</span>
                      <span className="font-medium">{contract.stroomprijs.vastrecht}</span>
                    </div>
                  </div>
                </div>

                {/* Gasprijs */}
                <div className="bg-orange-50 dark:bg-orange-950/20 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-3">Gasprijs</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Kale prijs:</span>
                      <span className="font-medium">{contract.gasprijs.kale}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Inkoopvergoeding:</span>
                      <span className="font-medium">{contract.gasprijs.inkoopvergoeding}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Vastrecht:</span>
                      <span className="font-medium">{contract.gasprijs.vastrecht}</span>
                    </div>
                  </div>
                </div>

                {/* Handelen */}
                <div className="bg-gray-50 dark:bg-gray-950/20 rounded-lg p-4 flex items-center justify-between">
                  <span className="font-semibold">Handelen:</span>
                  {contract.handelen ? (
                    <div className="text-right">
                      <Badge variant="secondary" className="mb-1">Mogelijk</Badge>
                      {contract.handelsprijs && (
                        <p className="text-sm text-muted-foreground">
                          Via EasySMART: {contract.handelsprijs}
                        </p>
                      )}
                    </div>
                  ) : (
                    <Badge variant="outline">Niet mogelijk</Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg mb-6">
            Voor meer informatie neem gerust contact op met ons. Wij leggen het u uit en helpen u bij overstappen!
          </p>
          <Button 
            size="lg"
            onClick={() => window.open('https://calendly.com/sol-vi/new-meeting?primary_color=ff9429', '_blank')}
          >
            Plan Gratis Gesprek
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EnergyContractsSection;